import {getTestProps} from '@benchling/js-test-utils';
import {Box, Checkbox, Input, TextInput, Stack} from '@mantine/core';
import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';
import SearchInput from 'search/inputs/SearchInput';
import {JSONValue, Override} from 'util/Types';

import {HelpButtonOrNull} from '../editors/SectionName';
import {StepTypeConfigUISchemaV2} from '../step-config-settings/StepConfigUISchemas';
import {
  CUSTOM_COMPONENT_KEY,
  CustomComponentEditorProps,
  FIELD_ORDER_UI_SCHEMA_KEY,
} from '../StepConfigEditorTypes';

const REQUIRE_COLUMN_MAPPING_TOOLTIP =
  'If checked, users of the template must map the columns of the input table to the columns of the reference ' +
  'dataset. If unchecked, column mapping is configured within the template.';

type VariableTemplateInputTableConfig = PipelineStepConfig<
  PipelineStepType.VARIABLE_TEMPLATE_INPUT_TABLE,
  0,
  'draft'
>;

type VariableTemplateInputTableEditorProps = Override<
  CustomComponentEditorProps,
  {config: VariableTemplateInputTableConfig}
>;

/**
 * Custom editor for `VARIABLE_TEMPLATE_INPUT_TABLE` steps.
 *
 * This is also used in the `ConfigureTemplateInputPhase` right sidebar.
 */
function VariableTemplateInputTableEditor({
  config,
  onChangeConfig,
  disabled,
}: VariableTemplateInputTableEditorProps): JSX.Element {
  const datasetId = config.datasetId ?? null;

  const change = (path: ReadonlyArray<string>, newValue: JSONValue): void => {
    onChangeConfig(config, [...path], newValue);
  };

  return (
    <Stack spacing="md">
      <Input.Wrapper
        id={datasetId ?? undefined}
        label="Reference dataset"
        description={
          <Box mb={6}>
            Used to test the analysis as you design the template. Users map to these columns when applying the
            template.
          </Box>
        }
        withAsterisk={true}
        inputWrapperOrder={['label', 'description', 'input']}
      >
        <SearchInput
          id={datasetId ?? undefined}
          autoFocus={false}
          disableTyping={datasetId != null}
          isMulti={false}
          onRemove={() => {
            change(['datasetId'], null);
          }}
          onSelect={(id) => {
            change(['datasetId'], id);
          }}
          placeholder=""
          selected={datasetId == null ? [] : [datasetId]}
          types={[ModelTypeConstants.DATASET]}
          disabled={disabled}
        />
      </Input.Wrapper>
      <Checkbox
        label={
          <>
            Require user to map columns when applying template
            <HelpButtonOrNull infoText={REQUIRE_COLUMN_MAPPING_TOOLTIP} isCompact={true} />
          </>
        }
        checked={config.requireColumnMapping ?? false}
        onChange={(e) => {
          change(['requireColumnMapping'], e.currentTarget.checked);
        }}
        disabled={disabled}
        {...getTestProps({
          component: 'AddTemplateInputSourceRightSidebar',
          element: 'requireColumnMappingCheckbox',
        })}
      />
      <TextInput
        label="Description"
        description={<Box mb={6}>Describe what data is expected so users know what to provide</Box>}
        inputWrapperOrder={['label', 'description', 'input']}
        placeholder="e.g. Select a dataset with columns for..."
        value={config.description ?? ''}
        onChange={(e) => {
          change(['description'], e.target.value);
        }}
        disabled={disabled}
        {...getTestProps({
          component: 'AddTemplateInputSourceRightSidebar',
          element: 'descriptionInput',
        })}
      />
      <TextInput
        label="Table name"
        withAsterisk={true}
        value={config.name ?? ''}
        onChange={(e) => {
          change(['name'], e.target.value);
        }}
        disabled={disabled}
        {...getTestProps({
          component: 'AddTemplateInputSourceRightSidebar',
          element: 'nameInput',
        })}
      />
    </Stack>
  );
}

export const VARIABLE_TEMPLATE_INPUT_TABLE_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.VARIABLE_TEMPLATE_INPUT_TABLE,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['customEditor'],
  customEditor: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['customEditor'],
    customEditor: {
      displayName: '',
      [CUSTOM_COMPONENT_KEY]: VariableTemplateInputTableEditor,
    },
  },
};
